import { useQuery } from '@tanstack/react-query';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';
import dataService from 'services/dataService';
import { API_MEDICATION } from 'services/dataService/ccmResources';
import { showResult } from 'infrastructure/hooks/utils';

import type { SortState } from 'infrastructure/types';
import type {
  IMedicationResponse,
  IPartialPaginationMeta,
} from 'infrastructure/interfaces';

interface Props {
  patientGuid: string;
  meta: IPartialPaginationMeta;
  isActive?: boolean;
  documentationTimeSort?: SortState;
  sessionId?: string | null;
}

type TFilter = {
  patientGuid: string;
  isActive?: boolean;
  documentationTimeSort?: string;
  sessionId?: string | null;
} & IPartialPaginationMeta;

const getMedicationFn = async (filter: TFilter) => {
  const url = API_MEDICATION;
  const { error, data } = await dataService.getList<IMedicationResponse>(
    apiUrlGenerator(url, filter),
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

export const getMedicationKey = (filter: TFilter) => ['getMedication', filter];

export const MEDICATION_PER_PAGE = 5;

export const useGetMedication = ({
  patientGuid,
  meta,
  isActive,
  documentationTimeSort,
  sessionId,
}: Props) => {
  const filter: TFilter = {
    ...meta,
    patientGuid,
    isActive,
    documentationTimeSort: documentationTimeSort?.toUpperCase(),
    sessionId,
  };

  const {
    data: medication,
    isLoading,
    ...other
  } = useQuery({
    queryKey: getMedicationKey(filter),
    queryFn: async () => getMedicationFn(filter),
    staleTime: 10_000,
  });

  return {
    medication,
    isLoading,
    ...other,
  };
};
